import { useState } from 'react';
import Spinner from '../Spinner';
import intl from 'components/i18n/ReactIntlWrapper';
import {
	Typography,
	Box,
	Card,
	Chip,
} from '@mui/material';
import { DataGrid, GridColDef, GridInitialState } from '@mui/x-data-grid';
import SearchInput from 'components/SearchInput';
import { DEFAULT_PAGINATION, useSearch } from 'hooks/useSearch';
import { useApi } from '../../api/ApiProvider';
import Api from '../../axiosApi/api';
import { TeamMemberDTO } from '../../axiosApi/models';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';

const MyTeam = () => {
	const api: Api = useApi();
	const toast = useToastMessageQueue();
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [errors, setErrors] = useState({});

	const apiPromise = (
		searchTerm: string,
		pageNum: number,
		pageSize: number,
		sort: string,
		order: string
	) => {
		return api.employeeApi.apiVversionEmployeeTeamGet("1", pageNum, pageSize, searchTerm, sort, order);
	};

	const processError = (error: any) => {
		handleAPIError(error, toast, errors);
		setErrors({ ...errors });
	};

	const { data, loading, onPaginationModelChange, handleSortModelChange, paginationModel } = useSearch<TeamMemberDTO>({
		apiPromise: apiPromise,
		searchTerm: searchTerm,
		defaultSort: { sort: 'Name', order: 'asc' },
		onError: processError,
		pageKey: 'myTeam',
	});

	const handleClearSearch = () => {
		setSearchTerm('');
	};

	const INITIAL_STATE: GridInitialState = {
		pagination: {
			paginationModel: {
				page: DEFAULT_PAGINATION.pageNum,
				pageSize: DEFAULT_PAGINATION.pageSize,
			},
		},
		sorting: {
			sortModel: [
				{
					field: 'name',
					sort: 'asc',
				},
			],
		},
	};

	const columns: GridColDef[] = [
		{
			field: 'name',
			headerName: intl.get('employeeList.table.thead.name') || 'Name',
			width: 250,
			flex: 1,
		},
		{
			field: 'lastName',
			headerName: intl.get('employeeList.table.thead.lastName') || 'Last Name',
			width: 250,
			flex: 1,
		},
		{
			field: 'currentProjects',
			headerName: intl.get('myTeam.table.thead.project') || 'Project',
			width: 250,
			flex: 1,
			sortable: false,
			renderCell: (params) => {
				const projects = params.row.currentProjects;
				if (!projects || projects.length === 0) return null;
				return (
					<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', marginTop: 1 }}>
						{projects.map((project: string, index: number) => (
							<Chip
								key={index}
								label={project}
								color="primary"
								size="small"
							/>
						))}
					</Box>
				);
			},
		},
	];

	return (
		<Card sx={{ m: 2 }}>
			<Box sx={{ p: 3 }}>
				<Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
					{intl.get('myTeam.header') || 'My Teams'}
				</Typography>
				<Box sx={{ p: 3 }}>
					<Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} mb={2}>
						<Box display={'flex'} alignItems={'center'}>
							<SearchInput value={searchTerm} onChange={setSearchTerm} onClear={handleClearSearch} />
						</Box>
					</Box>
					<Box style={{ width: '100%', display: 'flex' }}>
						{loading && !data && <Spinner />}
						{data && (
							<DataGrid
								rows={data.queryResult || []}
								columns={columns}
								rowCount={data.totalRecords || 0}
								loading={loading}
								paginationModel={paginationModel}
								onPaginationModelChange={onPaginationModelChange}
								onSortModelChange={handleSortModelChange}
								pageSizeOptions={[5, 10, 25]}
							/>
						)}
					</Box>
				</Box>
			</Box>
		</Card>
	);
};

export default MyTeam;

